import { useState, useEffect } from 'react';
import axios from 'axios';
import { LOAD_TIMEOUT_MS } from './loading';

export interface Sponsor {
    id: string;
    name: string;
    websiteUrl: string;
    logoUrl: string;
    order?: number;
}

interface UseSponsorsResult {
    sponsors: Sponsor[] | undefined;
    loading: boolean;
    error: string | undefined;
}

export function useSponsors(eventKey: string | undefined): UseSponsorsResult {
    const [sponsors, setSponsors] = useState<Sponsor[] | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>(undefined);

    useEffect(() => {
        // If no eventKey, return empty array
        if (!eventKey) {
            setSponsors([]);
            setLoading(false);
            setError(undefined);
            return;
        }

        let active = true;
        const cancelSource = axios.CancelToken.source();
        setLoading(true);
        setError(undefined);

        var apiPath = '/api/events/display/sponsors';
        if (process.env.REACT_APP_FIREBASE_KEY === 'development') {
            apiPath = 'http://localhost:3000' + apiPath;
        }

        axios
            .post(apiPath, { key: eventKey }, { timeout: LOAD_TIMEOUT_MS, cancelToken: cancelSource.token })
            .then((response) => {
                if (!active) return;
                if (response.status === 200) {
                    const responseData = response.data || {};
                    const sponsorsArray = responseData.sponsors || [];
                    
                    // Map API response to Sponsor interface, ensuring required fields
                    const mappedSponsors: Sponsor[] = sponsorsArray
                        .filter((sponsor: any) => {
                            // Check for required fields - API uses logoUrl and websiteUrl
                            return sponsor.name && sponsor.websiteUrl && sponsor.logoUrl;
                        })
                        .map((sponsor: any) => ({
                            id: sponsor.id || '',
                            name: sponsor.name || '',
                            websiteUrl: sponsor.websiteUrl || '',
                            logoUrl: sponsor.logoUrl || '',
                            order: sponsor.order,
                        }));
                    
                    setSponsors(mappedSponsors);
                } else {
                    setSponsors([]);
                }
            })
            .catch((err) => {
                if (!active) return;
                setError(err.message || 'Failed to fetch sponsors');
                setSponsors([]);
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => { active = false; cancelSource.cancel(); };
    }, [eventKey]);

    return { sponsors, loading, error };
}

